/**
 * `resoudreFigure(spec) → FigureResolue | null` — F3 de
 * `docs/produit/SPEC-REPRESENTATION-VISUELLE.md` (§3.4.3).
 *
 * **Troisième et dernière couche de refus**, après Zod et `figureCoherente()`.
 * Son principe tient en une phrase : *une figure impossible est une figure
 * sans solution*. L'inégalité triangulaire, la somme des angles, l'hypoténuse
 * plus courte qu'une cathète ne sont donc pas une liste de règles ad hoc —
 * elles se manifestent toutes comme une ABSENCE DE SOLUTION, et `null` est le
 * refus. Une figure fausse enseigne une erreur et l'élève n'a aucun moyen de
 * la contester : elle est l'énoncé (§3.5). Jamais de rattrapage silencieux.
 *
 * Fonction PURE, sans dépendance, partagée client et serveur : c'est ce qui
 * garantit que le SVG de l'app et l'image du courriel disent la même chose.
 *
 * Trois règles portées ici, chacune imposée par la maquette :
 *
 *  - **H4 — la rotation ne tourne que la géométrie.** `rotationDeg` s'applique
 *    aux points, aux arcs et aux ANCRES de texte ; les textes eux-mêmes restent
 *    horizontaux. Sans cela, le triangle à 145° de G7 (b) serait illisible.
 *  - **H5 — le placement des étiquettes est déterministe, et il évite les
 *    collisions.** Chaque texte est posé le long de la normale sortante de
 *    l'arête ou du sommet ancré, à un dégagement proportionnel à la diagonale
 *    de la figure — c'est la position CANDIDATE N°0 de
 *    `resoudreCollisionsEtiquettes()` (`etiquettes.ts`). Si elle est libre,
 *    elle est retenue telle quelle : une figure sans conflit sort du placeur
 *    inchangée. Sinon le placeur essaie, dans un ordre fixe, huit orientations
 *    sur trois anneaux, puis déporte l'étiquette avec un filet de rappel. La
 *    limite assumée de H5 — « sur un triangle très aplati, deux étiquettes
 *    peuvent se serrer » — est ainsi levée, sans jamais toucher au TEXTE :
 *    `decrireFigure()` décrit la figure, pas sa mise en page.
 *  - **H8 — les échelles x et y sont égales.** `echelle.parUnite` est un
 *    scalaire unique : le solveur ajuste la boîte, jamais le rapport d'échelle.
 *    Sans cela, le cercle de tolérance de G6 serait une ellipse.
 *
 * **H1 — la couche `annotations` ne passe jamais par le refus géométrique.**
 * Elle est posée par-dessus la figure résolue ; un ancrage qui ne désigne rien
 * est ignoré, jamais un motif de refus. C'est ce qui rend représentable la
 * figure-erreur de G3/G4/G7, géométriquement fausse par construction.
 */
import { PRIORITE_ETIQUETTE, resoudreCollisionsEtiquettes, } from './etiquettes.js';
import { angleDe, barycentre, formaterNombre, fois, milieu, moins, normale, norme, plus, projete, rad, surCercle, tourner, tousFinis, unitaire, vec, } from './geometrie.js';
import { classerAretes, developpementDe, ellipseHorizontale, polyedreDe, projeter, tangentesBase, } from './solides.js';
/* ------------------------------------------------------------- constantes */
/** Largeur canonique de la boîte résolue, en unités d'affichage. Les épaisseurs de trait et la taille de police de la maquette (§12.1) sont littérales dans ce cadre. */
export const LARGEUR_RESOLUE = 320;
/** Rapport hauteur/largeur maximal : au-delà, la boîte est ÉLARGIE (jamais la géométrie déformée). */
const RATIO_MAX = 1.3;
/** Rapport minimal : en deçà, la boîte est HAUSSÉE. */
const RATIO_MIN = 0.42;
/** Marge autour de la géométrie, en fraction de sa plus grande dimension. */
const MARGE = 0.06;
/** Taille de police des étiquettes dans la boîte canonique (`.lbl`, §12.1). */
export const TAILLE_POLICE_RESOLUE = 13;
/** Largeur moyenne d'un caractère d'IBM Plex Mono, en fraction de la taille de police. */
const LARGEUR_CARACTERE = 0.65;
/**
 * Décalage de la ligne de base d'un texte sous son ancre, en fraction de la
 * taille de police : l'ancre est une POSITION, pas une ligne de base, et le
 * texte y est centré verticalement. Le renderer applique exactement la même
 * valeur — les deux doivent rester d'accord, sinon la boîte calculée ici
 * rogne le rendu.
 */
export const LIGNE_BASE_TEXTE = 0.36;
/** Montante et descendante d'IBM Plex Mono, en fraction de la taille de police. */
const MONTANTE = 0.78;
const DESCENDANTE = 0.3;
/** Dégagement d'une étiquette par rapport à son ancre, en fraction de la diagonale de la géométrie (H5). */
const DEGAGEMENT = 0.055;
/** Nombre d'échantillons d'une courbe de fonction sur le domaine visible. */
const ECHANTILLONS_COURBE = 160;
function brutVide() {
    return {
        traces: [],
        marques: [],
        textes: [],
        rappels: [],
        points: [],
        elementsAncres: [],
        centre: vec(0, 0),
        diagonale: 1,
        pivot: vec(0, 0),
        rotationPermise: false,
        unite: null,
    };
}
function trace(b, forme, trait, ton = 'neutre', style = 'plein', remplie = false) {
    b.traces.push({ forme, trait, ton, style, remplie });
}
function segment(b, de, a, trait = 'principal', ton = 'neutre', style = 'plein') {
    trace(b, { type: 'polyligne', points: [de, a], fermee: false }, trait, ton, style);
}
function polyligne(b, points, fermee, trait = 'principal', ton = 'neutre', style = 'plein', remplie = false) {
    trace(b, { type: 'polyligne', points: [...points], fermee }, trait, ton, style, remplie);
}
function marque(b, position, rayonRelatif = 0.011, ton = 'neutre') {
    b.marques.push({ position, rayon: b.diagonale * rayonRelatif, ton });
}
/**
 * Pose un texte à un DÉGAGEMENT fixe de son ancre, le long de `direction`
 * (H5). L'alignement horizontal découle du signe de la direction ; le texte
 * lui-même n'est jamais pivoté.
 */
function poserTexte(b, ancre, direction, texte, ton = 'neutre', facteur = 1, priorite = PRIORITE_ETIQUETTE.identite) {
    if (texte.length === 0)
        return;
    const u = unitaire(direction) ?? vec(0, 1);
    const d = b.diagonale * DEGAGEMENT * facteur;
    const position = plus(ancre, fois(u, d));
    const alignement = u.x > 0.3 ? 'debut' : u.x < -0.3 ? 'fin' : 'milieu';
    b.textes.push({ position, texte, alignement, ton, ancre, direction: u, degagement: d, priorite });
}
/**
 * Pose un texte que le placeur ne DOIT PAS déplacer : une graduation d'axe,
 * une catégorie sous sa bande, une valeur au-dessus de sa bande. Elle reste
 * exactement là où le tracé la met, et sert d'obstacle aux autres.
 */
function poserTexteFixe(b, position, texte, alignement, ton = 'neutre') {
    if (texte.length === 0)
        return;
    b.textes.push({
        position,
        texte,
        alignement,
        ton,
        ancre: null,
        direction: vec(0, 1),
        degagement: 0,
        priorite: PRIORITE_ETIQUETTE.donnee,
    });
}
/** Direction sortante depuis le centre de la figure — la normale « qui s'éloigne ». */
function sortante(b, p) {
    return unitaire(moins(p, b.centre)) ?? vec(0, 1);
}
/** Normale sortante d'une arête : la normale du segment orientée à l'opposé du centre. */
function normaleSortante(b, de, a) {
    const u = unitaire(moins(a, de));
    if (u === null)
        return vec(0, 1);
    const n = normale(u);
    const m = milieu(de, a);
    const versExterieur = moins(m, b.centre);
    return n.x * versExterieur.x + n.y * versExterieur.y >= 0 ? n : fois(n, -1);
}
/**
 * Normale d'un segment orientée VERS LE HAUT. Le centre d'un cercle est sur
 * son diamètre : la normale « sortante » y est indéfinie, et deux étiquettes
 * ancrées au même endroit finiraient l'une sur l'autre. On tranche donc
 * toujours du même côté — au-dessus du trait, comme la maquette.
 */
function normaleHaute(u) {
    const n = normale(u);
    return n.y >= 0 ? n : fois(n, -1);
}
/** Marque d'angle droit : le petit carré ouvert `.ln-2` de la maquette. */
function marqueAngleDroit(b, sommet, vers1, vers2, ton = 'neutre') {
    const u1 = unitaire(moins(vers1, sommet));
    const u2 = unitaire(moins(vers2, sommet));
    if (u1 === null || u2 === null)
        return;
    const t = b.diagonale * 0.05;
    const p1 = plus(sommet, fois(u1, t));
    const p2 = plus(sommet, fois(u2, t));
    polyligne(b, [p1, plus(p1, fois(u2, t)), p2], false, 'secondaire', ton);
}
/** Arc de mesure d'angle, plus son étiquette « 35° ». */
function arcAngle(b, sommet, vers1, vers2, etiquette, ton = 'neutre') {
    const u1 = unitaire(moins(vers1, sommet));
    const u2 = unitaire(moins(vers2, sommet));
    if (u1 === null || u2 === null)
        return;
    const r = b.diagonale * 0.09;
    let a1 = angleDe(u1);
    let a2 = angleDe(u2);
    if ((a2 - a1 + 360) % 360 > 180) {
        const t = a1;
        a1 = a2;
        a2 = t;
    }
    trace(b, {
        type: 'arc',
        centre: sommet,
        rayon: r,
        debutDeg: a1,
        finDeg: a1 + ((a2 - a1 + 360) % 360),
        secteur: false,
    }, 'secondaire', ton);
    if (etiquette !== null) {
        const bissectrice = unitaire(plus(u1, u2));
        if (bissectrice !== null)
            poserTexte(b, sommet, bissectrice, etiquette, ton, 1.5, PRIORITE_ETIQUETTE.donnee);
    }
}
/** Marques d'égalité : les petits traits perpendiculaires portés par un côté. */
function marqueEgaliteCote(b, de, a) {
    const u = unitaire(moins(a, de));
    if (u === null)
        return;
    const n = normale(u);
    const m = milieu(de, a);
    const t = b.diagonale * 0.022;
    polyligne(b, [plus(m, fois(n, t)), plus(m, fois(n, -t))], false, 'secondaire');
}
function mesure(valeur, unite) {
    return `${formaterNombre(valeur)} ${unite}`;
}
function cleCote(x, y) {
    return x < y ? `${x}|${y}` : `${y}|${x}`;
}
/**
 * Convertit les DONNÉES DU PROBLÈME en trois longueurs de côté. Rend `null`
 * dès qu'aucun triangle ne satisfait les données : c'est là que vivent les
 * refus géométriques de la famille (§3.3.1).
 */
function longueursTriangle(spec) {
    const [A, B, C] = spec.sommets;
    const noms = new Set([A, B, C]);
    if (noms.size !== 3)
        return null;
    const cotes = new Map();
    const angles = new Map();
    for (const d of spec.donnees) {
        if (d.type === 'cote') {
            if (!noms.has(d.de) || !noms.has(d.a) || d.de === d.a)
                return null;
            const cle = cleCote(d.de, d.a);
            if (cotes.has(cle))
                return null; // deux côtés portant le même couple de sommets
            if (!(d.valeur > 0))
                return null;
            cotes.set(cle, d.valeur);
        }
        else {
            if (!noms.has(d.sommet))
                return null;
            if (angles.has(d.sommet))
                return null;
            angles.set(d.sommet, d.degres);
        }
    }
    const troisCotes = () => {
        const ab = cotes.get(cleCote(A, B));
        const bc = cotes.get(cleCote(B, C));
        const ca = cotes.get(cleCote(C, A));
        if (ab === undefined || bc === undefined || ca === undefined)
            return null;
        return valider({ ab, bc, ca });
    };
    /** Inégalité triangulaire STRICTE : `a + b <= c` pour une permutation ⇒ pas de triangle. */
    const valider = (t) => {
        const { ab, bc, ca } = t;
        if (!tousFinis(ab, bc, ca))
            return null;
        if (!(ab > 0 && bc > 0 && ca > 0))
            return null;
        if (ab + bc <= ca || bc + ca <= ab || ca + ab <= bc)
            return null;
        return t;
    };
    switch (spec.construction) {
        case 'trois_cotes': {
            if (spec.donnees.length !== 3 || cotes.size !== 3)
                return null;
            return troisCotes();
        }
        case 'deux_cotes_angle': {
            if (cotes.size !== 2 || angles.size !== 1)
                return null;
            const [sommetAngle] = [...angles.keys()];
            const theta = angles.get(sommetAngle);
            if (!(theta > 0 && theta < 180))
                return null;
            // Les deux côtés donnés doivent partager le sommet porteur de l'angle.
            const voisins = [];
            for (const cle of cotes.keys()) {
                const [x, y] = cle.split('|');
                if (x === sommetAngle)
                    voisins.push(y);
                else if (y === sommetAngle)
                    voisins.push(x);
                else
                    return null;
            }
            if (voisins.length !== 2)
                return null;
            const [P, Q] = voisins;
            const p = cotes.get(cleCote(sommetAngle, P));
            const q = cotes.get(cleCote(sommetAngle, Q));
            const pq = Math.sqrt(p * p + q * q - 2 * p * q * Math.cos(rad(theta)));
            const table = new Map([
                [cleCote(sommetAngle, P), p],
                [cleCote(sommetAngle, Q), q],
                [cleCote(P, Q), pq],
            ]);
            return valider({
                ab: table.get(cleCote(A, B)),
                bc: table.get(cleCote(B, C)),
                ca: table.get(cleCote(C, A)),
            });
        }
        case 'cote_deux_angles': {
            if (cotes.size !== 1 || angles.size !== 2)
                return null;
            const [cle] = [...cotes.keys()];
            const c = cotes.get(cle);
            const [X, Y] = cle.split('|');
            const alpha = angles.get(X);
            const beta = angles.get(Y);
            // Les deux angles doivent être aux DEUX EXTRÉMITÉS du côté donné.
            if (alpha === undefined || beta === undefined)
                return null;
            if (!(alpha > 0 && beta > 0))
                return null;
            if (alpha + beta >= 180)
                return null; // somme des angles : pas de troisième sommet
            const gamma = 180 - alpha - beta;
            const Z = [A, B, C].find((n) => n !== X && n !== Y);
            const yz = (c * Math.sin(rad(alpha))) / Math.sin(rad(gamma));
            const xz = (c * Math.sin(rad(beta))) / Math.sin(rad(gamma));
            const table = new Map([
                [cleCote(X, Y), c],
                [cleCote(Y, Z), yz],
                [cleCote(X, Z), xz],
            ]);
            return valider({
                ab: table.get(cleCote(A, B)),
                bc: table.get(cleCote(B, C)),
                ca: table.get(cleCote(C, A)),
            });
        }
        case 'rectangle': {
            const S = spec.sommetAngleDroit;
            if (S === undefined || !noms.has(S))
                return null;
            if (spec.donnees.length !== 2)
                return null;
            if (angles.size === 2)
                return null; // sur-détermination : taille indéterminée
            if (angles.has(S))
                return null; // l'angle droit est le 3ᵉ fait, implicite
            for (const a of angles.values())
                if (!(a > 0 && a < 90))
                    return null;
            const [P, Q] = [A, B, C].filter((n) => n !== S);
            const cathete1 = cotes.get(cleCote(S, P));
            const cathete2 = cotes.get(cleCote(S, Q));
            const hypotenuse = cotes.get(cleCote(P, Q));
            let sp = null;
            let sq = null;
            if (cathete1 !== undefined && cathete2 !== undefined) {
                sp = cathete1;
                sq = cathete2;
            }
            else if (hypotenuse !== undefined && (cathete1 !== undefined || cathete2 !== undefined)) {
                const cathete = (cathete1 ?? cathete2);
                // Refus : une hypoténuse plus courte (ou égale) qu'une cathète — le
                // triangle rectangle n'existe pas, et aucun rattrapage n'est permis.
                if (hypotenuse <= cathete)
                    return null;
                const autre = Math.sqrt(hypotenuse * hypotenuse - cathete * cathete);
                if (cathete1 !== undefined) {
                    sp = cathete1;
                    sq = autre;
                }
                else {
                    sp = autre;
                    sq = cathete2;
                }
            }
            else if (angles.size === 1) {
                const [sommetAngle] = [...angles.keys()];
                const theta = angles.get(sommetAngle);
                // L'angle aigu est à P ou à Q ; côté opposé / côté adjacent depuis ce sommet.
                const opposeAuSommet = sommetAngle === P ? Q : P;
                if (sommetAngle !== P && sommetAngle !== Q)
                    return null;
                const cathAdj = cotes.get(cleCote(S, sommetAngle));
                const cathOpp = cotes.get(cleCote(S, opposeAuSommet));
                const table = new Map();
                if (cathAdj !== undefined) {
                    table.set(cleCote(S, sommetAngle), cathAdj);
                    table.set(cleCote(S, opposeAuSommet), cathAdj * Math.tan(rad(theta)));
                }
                else if (cathOpp !== undefined) {
                    table.set(cleCote(S, opposeAuSommet), cathOpp);
                    table.set(cleCote(S, sommetAngle), cathOpp / Math.tan(rad(theta)));
                }
                else if (hypotenuse !== undefined) {
                    table.set(cleCote(S, opposeAuSommet), hypotenuse * Math.sin(rad(theta)));
                    table.set(cleCote(S, sommetAngle), hypotenuse * Math.cos(rad(theta)));
                }
                else
                    return null;
                sp = table.get(cleCote(S, P));
                sq = table.get(cleCote(S, Q));
            }
            else
                return null;
            if (sp === null || sq === null || !tousFinis(sp, sq) || sp <= 0 || sq <= 0)
                return null;
            const pq = Math.hypot(sp, sq);
            const table = new Map([
                [cleCote(S, P), sp],
                [cleCote(S, Q), sq],
                [cleCote(P, Q), pq],
            ]);
            return valider({
                ab: table.get(cleCote(A, B)),
                bc: table.get(cleCote(B, C)),
                ca: table.get(cleCote(C, A)),
            });
        }
        default:
            return null;
    }
}
function resoudreTriangle(spec) {
    const cotes = longueursTriangle(spec);
    if (cotes === null)
        return null;
    const [A, B, C] = spec.sommets;
    // Placement canonique : le premier sommet à l'origine, le deuxième sur +x,
    // le troisième au-dessus. Pour un triangle rectangle, on pose le sommet de
    // l'angle droit à l'origine et ses deux cathètes sur les axes — c'est la
    // figure du vocabulaire de la maquette (angle droit en bas à gauche).
    const positions = new Map();
    if (spec.construction === 'rectangle' && spec.sommetAngleDroit !== undefined) {
        const S = spec.sommetAngleDroit;
        const [P, Q] = [A, B, C].filter((n) => n !== S);
        const longueur = (x, y) => {
            const cle = cleCote(x, y);
            if (cle === cleCote(A, B))
                return cotes.ab;
            if (cle === cleCote(B, C))
                return cotes.bc;
            return cotes.ca;
        };
        const lp = longueur(S, P);
        const lq = longueur(S, Q);
        // La plus longue cathète à l'horizontale : la figure est posée à plat.
        const [horizontal, vertical] = lp >= lq ? [P, Q] : [Q, P];
        positions.set(S, vec(0, 0));
        positions.set(horizontal, vec(longueur(S, horizontal), 0));
        positions.set(vertical, vec(0, longueur(S, vertical)));
    }
    else {
        const { ab, bc, ca } = cotes;
        const x = (ab * ab + ca * ca - bc * bc) / (2 * ab);
        const y2 = ca * ca - x * x;
        if (!(y2 > 0))
            return null; // triangle plat : aucune solution
        positions.set(A, vec(0, 0));
        positions.set(B, vec(ab, 0));
        positions.set(C, vec(x, Math.sqrt(y2)));
    }
    const pts = [A, B, C].map((n) => positions.get(n));
    if (pts.some((p) => !tousFinis(p.x, p.y)))
        return null;
    const b = brutVide();
    b.rotationPermise = true;
    b.unite = spec.donnees.find((d) => d.type === 'cote')?.unite ?? 'u';
    b.centre = barycentre(pts);
    b.pivot = b.centre;
    b.diagonale = Math.max(cotes.ab, cotes.bc, cotes.ca);
    for (const nom of [A, B, C])
        b.points.push({ nom, position: positions.get(nom) });
    polyligne(b, pts, true);
    for (const p of pts)
        marque(b, p);
    // Marque d'angle droit — propriété de la figure, pas une annotation.
    if (spec.construction === 'rectangle' && spec.sommetAngleDroit !== undefined) {
        const S = spec.sommetAngleDroit;
        const [P, Q] = [A, B, C].filter((n) => n !== S);
        marqueAngleDroit(b, positions.get(S), positions.get(P), positions.get(Q));
    }
    // Les trois lettres de sommet sont TOUJOURS dessinées (§3.3.1).
    for (const nom of [A, B, C]) {
        const p = positions.get(nom);
        poserTexte(b, p, sortante(b, p), nom);
    }
    // Le renderer écrit AUTOMATIQUEMENT la valeur de chaque donnée (§3.3.1).
    for (const d of spec.donnees) {
        if (d.type === 'cote') {
            const de = positions.get(d.de);
            const a = positions.get(d.a);
            poserTexte(b, milieu(de, a), normaleSortante(b, de, a), mesure(d.valeur, d.unite), 'neutre', 1, PRIORITE_ETIQUETTE.donnee);
        }
        else {
            const s = positions.get(d.sommet);
            const [v1, v2] = [A, B, C]
                .filter((n) => n !== d.sommet)
                .map((n) => positions.get(n));
            arcAngle(b, s, v1, v2, `${formaterNombre(d.degres)}°`, 'accent');
        }
    }
    for (const anc of spec.marquesEgalite ?? []) {
        if (anc.type === 'cote') {
            const de = positions.get(anc.de);
            const a = positions.get(anc.a);
            if (de !== undefined && a !== undefined)
                marqueEgaliteCote(b, de, a);
        }
        else if (anc.type === 'angle' || anc.type === 'sommet') {
            const nom = anc.type === 'angle' ? anc.sommet : anc.nom;
            const s = positions.get(nom);
            if (s !== undefined) {
                const [v1, v2] = [A, B, C].filter((n) => n !== nom).map((n) => positions.get(n));
                arcAngle(b, s, v1, v2, null);
            }
        }
    }
    if (spec.hauteurDepuis !== undefined) {
        const s = positions.get(spec.hauteurDepuis);
        const [x1, x2] = [A, B, C]
            .filter((n) => n !== spec.hauteurDepuis)
            .map((n) => positions.get(n));
        if (s !== undefined) {
            const pied = projete(s, x1, x2);
            if (pied === null)
                return null;
            segment(b, s, pied, 'secondaire', 'neutre', 'pointille');
            marqueAngleDroit(b, pied, s, x1);
        }
    }
    poserEtiquettes(b, spec.etiquettes, (anc) => ancragePolygonal(b, anc, positions));
    return b;
}
/* ----------------------------------------------------------------- cercle */
function resoudreCercle(spec) {
    const r = spec.rayon;
    if (!tousFinis(r) || r <= 0)
        return null;
    const elements = spec.elements ?? [];
    if (elements.length > 3)
        return null;
    const b = brutVide();
    b.rotationPermise = true;
    b.unite = spec.unite;
    b.centre = vec(0, 0);
    b.pivot = vec(0, 0);
    b.diagonale = 2 * r;
    b.points.push({ nom: spec.nomCentre, position: vec(0, 0) });
    trace(b, { type: 'cercle', centre: vec(0, 0), rayon: r }, 'principal');
    marque(b, vec(0, 0));
    poserTexte(b, vec(0, 0), vec(0, -1), spec.nomCentre);
    if (spec.rayonAffiche === true) {
        const p = surCercle(vec(0, 0), r, 30);
        segment(b, vec(0, 0), p, 'secondaire');
        poserTexte(b, milieu(vec(0, 0), p), vec(0, 1), mesure(r, spec.unite), 'neutre', 0.7, PRIORITE_ETIQUETTE.donnee);
    }
    for (const el of elements) {
        const ton = el.ton ?? 'neutre';
        switch (el.type) {
            case 'rayon': {
                const p = surCercle(vec(0, 0), r, el.angleDeg);
                segment(b, vec(0, 0), p, 'principal', ton);
                b.elementsAncres.push(milieu(vec(0, 0), p));
                if (el.mesureAffichee === true) {
                    poserTexte(b, milieu(vec(0, 0), p), normaleHaute(unitaire(p) ?? vec(1, 0)), mesure(r, spec.unite), ton, 0.7, PRIORITE_ETIQUETTE.donnee);
                }
                break;
            }
            case 'diametre': {
                const p1 = surCercle(vec(0, 0), r, el.angleDeg);
                const p2 = surCercle(vec(0, 0), r, el.angleDeg + 180);
                segment(b, p1, p2, 'principal', ton);
                // Les deux petits traits d'about de la maquette (`.ln-2` aux extrémités).
                const u = unitaire(moins(p2, p1));
                if (u !== null) {
                    const n = fois(normale(u), r * 0.08);
                    polyligne(b, [plus(p1, n), moins(p1, n)], false, 'secondaire');
                    polyligne(b, [plus(p2, n), moins(p2, n)], false, 'secondaire');
                }
                b.elementsAncres.push(vec(0, 0));
                if (el.mesureAffichee === true) {
                    poserTexte(b, vec(0, 0), normaleHaute(unitaire(moins(p2, p1)) ?? vec(1, 0)), mesure(2 * r, spec.unite), ton, 0.7, PRIORITE_ETIQUETTE.donnee);
                }
                break;
            }
            case 'corde': {
                if ((((el.angleDeg2 - el.angleDeg1) % 360) + 360) % 360 === 0)
                    return null; // la corde serait un point
                const p1 = surCercle(vec(0, 0), r, el.angleDeg1);
                const p2 = surCercle(vec(0, 0), r, el.angleDeg2);
                segment(b, p1, p2, 'principal', ton);
                b.elementsAncres.push(milieu(p1, p2));
                if (el.mesureAffichee === true) {
                    const delta = rad(el.angleDeg2 - el.angleDeg1);
                    const longueur = 2 * r * Math.abs(Math.sin(delta / 2));
                    poserTexte(b, milieu(p1, p2), normaleSortante(b, p1, p2), mesure(longueur, spec.unite), ton, 0.7, PRIORITE_ETIQUETTE.donnee);
                }
                break;
            }
            case 'secteur':
            case 'arc': {
                const ouverture = el.angleFinDeg - el.angleDebutDeg;
                if (!(Math.abs(ouverture) > 0) || Math.abs(ouverture) > 360)
                    return null;
                const debut = Math.min(el.angleDebutDeg, el.angleFinDeg);
                const fin = Math.max(el.angleDebutDeg, el.angleFinDeg);
                // `remplie` est exactement `.aire` de la maquette : un remplissage
                // SANS trait. Un secteur émet donc deux tracés — l'aire, puis son
                // contour — plutôt qu'un seul tracé à la fois rempli et souligné.
                if (el.type === 'secteur') {
                    trace(b, {
                        type: 'arc',
                        centre: vec(0, 0),
                        rayon: r,
                        debutDeg: debut,
                        finDeg: fin,
                        secteur: true,
                    }, 'grille', ton, 'plein', true);
                }
                trace(b, {
                    type: 'arc',
                    centre: vec(0, 0),
                    rayon: r,
                    debutDeg: debut,
                    finDeg: fin,
                    secteur: el.type === 'secteur',
                }, 'principal', ton);
                const milieuAngle = (debut + fin) / 2;
                const ancre = surCercle(vec(0, 0), r, milieuAngle);
                b.elementsAncres.push(ancre);
                if (el.mesureAffichee === true) {
                    const longueur = r * Math.abs(rad(fin - debut));
                    poserTexte(b, ancre, unitaire(ancre) ?? vec(0, 1), mesure(longueur, spec.unite), ton, 0.7, PRIORITE_ETIQUETTE.donnee);
                }
                break;
            }
        }
    }
    poserEtiquettes(b, spec.etiquettes, (anc) => {
        if (anc.type === 'element')
            return b.elementsAncres[anc.index] ?? null;
        if (anc.type === 'sommet' && anc.nom === spec.nomCentre)
            return vec(0, 0);
        return null;
    });
    return b;
}
/* --------------------------------------------------------------- polygone */
function sommetsPolygone(spec) {
    const d = spec.dimensions;
    switch (d.forme) {
        case 'rectangle': {
            if (!(d.largeur > 0 && d.hauteur > 0))
                return null;
            return [vec(0, 0), vec(d.largeur, 0), vec(d.largeur, d.hauteur), vec(0, d.hauteur)];
        }
        case 'carre': {
            if (!(d.cote > 0))
                return null;
            return [vec(0, 0), vec(d.cote, 0), vec(d.cote, d.cote), vec(0, d.cote)];
        }
        case 'parallelogramme':
        case 'losange': {
            const base = d.forme === 'losange' ? d.cote : d.base;
            const cote = d.cote;
            if (!(base > 0 && cote > 0))
                return null;
            if (!(d.angleDeg > 0 && d.angleDeg < 180))
                return null;
            // Une figure nommée « parallélogramme » et dessinée comme un rectangle
            // enseigne une erreur : elle est REFUSÉE, pas corrigée (§3.3.3).
            if (Math.abs(d.angleDeg - 90) <= 0.5)
                return null;
            const dx = cote * Math.cos(rad(d.angleDeg));
            const dy = cote * Math.sin(rad(d.angleDeg));
            if (!(dy > 0))
                return null;
            return [vec(0, 0), vec(base, 0), vec(base + dx, dy), vec(dx, dy)];
        }
        case 'trapeze': {
            if (!(d.petiteBase > 0 && d.grandeBase > 0 && d.hauteur > 0))
                return null;
            if (d.petiteBase >= d.grandeBase)
                return null; // ce n'est plus la figure nommée
            if (Math.abs(d.decalage) > d.grandeBase)
                return null; // le trapèze se replie
            return [
                vec(0, 0),
                vec(d.grandeBase, 0),
                vec(d.decalage + d.petiteBase, d.hauteur),
                vec(d.decalage, d.hauteur),
            ];
        }
        case 'regulier': {
            if (!Number.isInteger(d.nbCotes) || d.nbCotes < 3 || d.nbCotes > 12)
                return null;
            if (!(d.cote > 0))
                return null;
            const n = d.nbCotes;
            const rayon = d.cote / (2 * Math.sin(Math.PI / n));
            if (!tousFinis(rayon) || rayon <= 0)
                return null;
            // Départ décalé d'un demi-pas : l'arête du bas est horizontale.
            const depart = -90 + 180 / n;
            return Array.from({ length: n }, (_, k) => surCercle(vec(0, 0), rayon, depart + (k * 360) / n));
        }
        default:
            return null;
    }
}
function resoudrePolygone(spec) {
    const pts = sommetsPolygone(spec);
    if (pts === null)
        return null;
    if (spec.sommets.length !== pts.length)
        return null;
    if (new Set(spec.sommets).size !== spec.sommets.length)
        return null;
    if (spec.apothemeTrace === true && spec.dimensions.forme !== 'regulier')
        return null;
    const b = brutVide();
    b.rotationPermise = true;
    b.unite = spec.dimensions.unite;
    b.centre = barycentre(pts);
    b.pivot = b.centre;
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    b.diagonale = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    const positions = new Map();
    spec.sommets.forEach((nom, i) => {
        positions.set(nom, pts[i]);
        b.points.push({ nom, position: pts[i] });
    });
    polyligne(b, pts, true);
    for (const p of pts)
        marque(b, p);
    for (const nom of spec.sommets) {
        const p = positions.get(nom);
        poserTexte(b, p, sortante(b, p), nom);
    }
    const affichees = new Set(spec.mesuresAffichees ?? ['base', 'cote', 'hauteur', 'angle']);
    const d = spec.dimensions;
    const cote = (i, j) => [pts[i], pts[j]];
    const etiqueterCote = (i, j, texte) => {
        const [p, q] = cote(i, j);
        poserTexte(b, milieu(p, q), normaleSortante(b, p, q), texte, 'neutre', 1, PRIORITE_ETIQUETTE.donnee);
    };
    if (d.forme === 'rectangle') {
        if (affichees.has('base'))
            etiqueterCote(0, 1, mesure(d.largeur, d.unite));
        if (affichees.has('hauteur'))
            etiqueterCote(1, 2, mesure(d.hauteur, d.unite));
    }
    else if (d.forme === 'carre') {
        if (affichees.has('cote'))
            etiqueterCote(0, 1, mesure(d.cote, d.unite));
    }
    else if (d.forme === 'parallelogramme') {
        if (affichees.has('base'))
            etiqueterCote(0, 1, mesure(d.base, d.unite));
        if (affichees.has('cote'))
            etiqueterCote(1, 2, mesure(d.cote, d.unite));
        if (affichees.has('angle'))
            arcAngle(b, pts[0], pts[1], pts[3], `${formaterNombre(d.angleDeg)}°`, 'accent');
    }
    else if (d.forme === 'losange') {
        if (affichees.has('cote'))
            etiqueterCote(0, 1, mesure(d.cote, d.unite));
        if (affichees.has('angle'))
            arcAngle(b, pts[0], pts[1], pts[3], `${formaterNombre(d.angleDeg)}°`, 'accent');
    }
    else if (d.forme === 'trapeze') {
        if (affichees.has('base')) {
            etiqueterCote(0, 1, mesure(d.grandeBase, d.unite));
            etiqueterCote(2, 3, mesure(d.petiteBase, d.unite));
        }
    }
    else if (d.forme === 'regulier') {
        if (affichees.has('cote'))
            etiqueterCote(0, 1, mesure(d.cote, d.unite));
    }
    // Hauteur tracée en pointillé, avec sa marque d'angle droit.
    if (spec.hauteurTracee === true &&
        (d.forme === 'trapeze' || d.forme === 'parallelogramme' || d.forme === 'losange')) {
        const sommetHaut = pts[3];
        const pied = projete(sommetHaut, pts[0], pts[1]);
        if (pied === null)
            return null;
        segment(b, sommetHaut, pied, 'secondaire', 'neutre', 'pointille');
        marqueAngleDroit(b, pied, sommetHaut, pts[1]);
        if (affichees.has('hauteur') && d.forme === 'trapeze') {
            poserTexte(b, milieu(sommetHaut, pied), vec(1, 0), mesure(d.hauteur, d.unite), 'neutre', 0.7, PRIORITE_ETIQUETTE.donnee);
        }
    }
    if (spec.apothemeTrace === true && d.forme === 'regulier') {
        const m = milieu(pts[0], pts[1]);
        segment(b, b.centre, m, 'secondaire', 'neutre', 'pointille');
        marqueAngleDroit(b, m, b.centre, pts[1]);
    }
    poserEtiquettes(b, spec.etiquettes, (anc) => ancragePolygonal(b, anc, positions));
    return b;
}
/* ----------------------------------------------------------------- repere */
/** Évalue une famille de fonction FERMÉE (`GRAPHE_FAMILLES`) — jamais une expression libre. */
export function evaluerGraphe(fn, x) {
    switch (fn.famille) {
        case 'lineaire':
            return fn.a * x + fn.b;
        case 'quadratique':
            return fn.a * x * x + fn.b * x + fn.c;
        case 'sinusoidale':
            return fn.a * Math.sin(fn.b * x + fn.c) + fn.d;
        case 'exponentielle':
            return fn.a * Math.pow(fn.b, x) + fn.c;
        default:
            return Number.NaN;
    }
}
function resoudreRepere(spec) {
    const { xMin, xMax, yMin, yMax } = spec.domaine;
    if (!tousFinis(xMin, xMax, yMin, yMax))
        return null;
    if (xMax <= xMin || yMax <= yMin)
        return null;
    if (xMax - xMin > 200 || yMax - yMin > 200)
        return null;
    if (!(spec.pas.x > 0) || !(spec.pas.y > 0))
        return null;
    if ((xMax - xMin) / spec.pas.x > 40 || (yMax - yMin) / spec.pas.y > 40)
        return null;
    if (spec.objets.length > 4)
        return null;
    if (spec.objets.filter((o) => o.type === 'courbe').length > 2)
        return null;
    const b = brutVide();
    b.rotationPermise = false; // un plan cartésien tourné mentirait sur ses axes
    b.unite = null;
    b.centre = vec((xMin + xMax) / 2, (yMin + yMax) / 2);
    b.pivot = b.centre;
    b.diagonale = Math.hypot(xMax - xMin, yMax - yMin);
    const dedans = (p) => p.x >= xMin - 1e-9 && p.x <= xMax + 1e-9 && p.y >= yMin - 1e-9 && p.y <= yMax + 1e-9;
    // Quadrillage.
    if (spec.grille !== false) {
        const grilleX = [];
        for (let x = Math.ceil(xMin / spec.pas.x) * spec.pas.x; x <= xMax + 1e-9; x += spec.pas.x) {
            grilleX.push([vec(x, yMin), vec(x, yMax)]);
        }
        for (let y = Math.ceil(yMin / spec.pas.y) * spec.pas.y; y <= yMax + 1e-9; y += spec.pas.y) {
            grilleX.push([vec(xMin, y), vec(xMax, y)]);
        }
        for (const [p, q] of grilleX)
            polyligne(b, [p, q], false, 'grille');
    }
    // Axes : posés sur zéro s'il est dans le domaine, sinon sur le bord.
    /** Abscisse de l'axe vertical et ordonnée de l'axe horizontal : zéro s'il est dans le domaine, sinon le bord. */
    const axeX = Math.min(Math.max(0, xMin), xMax);
    const axeY = Math.min(Math.max(0, yMin), yMax);
    polyligne(b, [vec(xMin, axeY), vec(xMax, axeY)], false, 'secondaire');
    polyligne(b, [vec(axeX, yMin), vec(axeX, yMax)], false, 'secondaire');
    // Graduations chiffrées — des MESURES d'énoncé, jamais un pourcentage.
    for (let x = Math.ceil(xMin / spec.pas.x) * spec.pas.x; x <= xMax + 1e-9; x += spec.pas.x) {
        const v = Number(x.toFixed(6));
        if (Math.abs(v - axeX) < 1e-9)
            continue;
        poserTexteFixe(b, vec(v, axeY - b.diagonale * 0.035), formaterNombre(v), 'milieu');
    }
    for (let y = Math.ceil(yMin / spec.pas.y) * spec.pas.y; y <= yMax + 1e-9; y += spec.pas.y) {
        const v = Number(y.toFixed(6));
        if (Math.abs(v - axeY) < 1e-9)
            continue;
        poserTexteFixe(b, vec(axeX - b.diagonale * 0.02, v), formaterNombre(v), 'fin');
    }
    poserTexteFixe(b, vec(axeX - b.diagonale * 0.02, axeY - b.diagonale * 0.035), '0', 'fin');
    const nommes = new Map();
    const resoudreRef = (r) => {
        if (r.type === 'coordonnees')
            return vec(r.x, r.y);
        return nommes.get(r.nom) ?? null;
    };
    for (const o of spec.objets) {
        const ton = 'ton' in o && o.ton !== undefined ? o.ton : 'neutre';
        const style = 'style' in o && o.style !== undefined ? o.style : 'plein';
        switch (o.type) {
            case 'point': {
                if (!dedans(o))
                    return null; // un point nommé hors du cadre est une figure fausse
                const p = vec(o.x, o.y);
                nommes.set(o.nom, p);
                b.points.push({ nom: o.nom, position: p });
                marque(b, p, 0.014, ton);
                const texte = o.coordonneesAffichees === true
                    ? `${o.nom} (${formaterNombre(o.x)} ; ${formaterNombre(o.y)})`
                    : o.nom;
                poserTexte(b, p, vec(1, 1), texte, ton, 0.6, o.coordonneesAffichees === true ? PRIORITE_ETIQUETTE.donnee : PRIORITE_ETIQUETTE.identite);
                break;
            }
            case 'segment': {
                const de = resoudreRef(o.de);
                const a = resoudreRef(o.a);
                if (de === null || a === null)
                    return null; // `nomme` sans point déclaré avant lui
                if (!dedans(de) || !dedans(a))
                    return null;
                if (norme(moins(a, de)) < 1e-9)
                    return null; // segment de longueur nulle
                polyligne(b, [de, a], false, 'principal', ton, style);
                if (o.etiquette !== undefined)
                    poserTexte(b, milieu(de, a), normale(unitaire(moins(a, de))), o.etiquette, ton, 0.6);
                break;
            }
            case 'droite': {
                const pts = [];
                for (const x of [xMin, xMax]) {
                    const y = o.a * x + o.b;
                    if (y >= yMin && y <= yMax)
                        pts.push(vec(x, y));
                }
                if (Math.abs(o.a) > 1e-9) {
                    for (const y of [yMin, yMax]) {
                        const x = (y - o.b) / o.a;
                        if (x > xMin && x < xMax)
                            pts.push(vec(x, y));
                    }
                }
                if (pts.length < 2)
                    return null; // la droite ne traverse pas le cadre
                const [p, q] = [pts[0], pts[pts.length - 1]];
                polyligne(b, [p, q], false, 'principal', ton, style);
                if (o.etiquette !== undefined)
                    poserTexte(b, milieu(p, q), normale(unitaire(moins(q, p))), o.etiquette, ton, 0.6, PRIORITE_ETIQUETTE.decor);
                break;
            }
            case 'vecteur': {
                const origine = resoudreRef(o.origine);
                if (origine === null)
                    return null;
                const extremite = plus(origine, vec(o.composantes.x, o.composantes.y));
                if (norme(vec(o.composantes.x, o.composantes.y)) < 1e-9)
                    return null; // vecteur nul
                if (!dedans(origine) || !dedans(extremite))
                    return null;
                polyligne(b, [origine, extremite], false, 'principal', ton, 'plein');
                // Pointe de flèche, en polyligne ouverte (motif exact de la maquette).
                const u = unitaire(moins(extremite, origine));
                const n = normale(u);
                const t = b.diagonale * 0.035;
                polyligne(b, [
                    plus(plus(extremite, fois(u, -t)), fois(n, t * 0.42)),
                    extremite,
                    plus(plus(extremite, fois(u, -t)), fois(n, -t * 0.42)),
                ], false, 'principal', ton);
                nommes.set(o.nom, extremite);
                const texte = o.composantesAffichees === true
                    ? `${o.nom} (${formaterNombre(o.composantes.x)} ; ${formaterNombre(o.composantes.y)})`
                    : o.nom;
                poserTexte(b, milieu(origine, extremite), n, texte, ton, 0.7, o.composantesAffichees === true ? PRIORITE_ETIQUETTE.donnee : PRIORITE_ETIQUETTE.identite);
                break;
            }
            case 'courbe': {
                const fn = o.fonction;
                const debut = Math.max(xMin, fn.domaine.xMin);
                const fin = Math.min(xMax, fn.domaine.xMax);
                if (!(fin > debut))
                    return null; // la courbe ne rencontre pas le cadre
                const morceaux = [];
                let courant = [];
                for (let i = 0; i <= ECHANTILLONS_COURBE; i += 1) {
                    const x = debut + ((fin - debut) * i) / ECHANTILLONS_COURBE;
                    const y = evaluerGraphe(fn, x);
                    if (Number.isFinite(y) && y >= yMin && y <= yMax) {
                        courant.push(vec(x, y));
                    }
                    else if (courant.length > 1) {
                        morceaux.push(courant);
                        courant = [];
                    }
                    else {
                        courant = [];
                    }
                }
                if (courant.length > 1)
                    morceaux.push(courant);
                if (morceaux.length === 0)
                    return null;
                for (const m of morceaux)
                    polyligne(b, m, false, 'principal', ton, style);
                for (const p of fn.pointsAMarquer ?? []) {
                    if (!dedans(p))
                        continue;
                    const q = vec(p.x, p.y);
                    marque(b, q, 0.014, ton);
                    poserTexte(b, q, vec(1, 1), p.etiquette, ton, 0.6, PRIORITE_ETIQUETTE.donnee);
                }
                if (o.etiquette !== undefined) {
                    const m = morceaux[morceaux.length - 1];
                    const p = m[m.length - 1];
                    poserTexte(b, p, vec(-1, 1), o.etiquette, ton, 0.7, PRIORITE_ETIQUETTE.decor);
                }
                break;
            }
        }
    }
    return b;
}
/* -------------------------------------------------------------- diagramme */
/** Boîte de tracé du diagramme, en unités naturelles (aucune unité de longueur : ce sont des données). */
const DIAG_L = 100;
const DIAG_H = 62;
function resoudreDiagramme(spec) {
    const b = brutVide();
    b.rotationPermise = false;
    b.unite = null;
    b.centre = vec(DIAG_L / 2, DIAG_H / 2);
    b.pivot = b.centre;
    b.diagonale = Math.hypot(DIAG_L, DIAG_H);
    if (spec.type === 'nuage_de_points') {
        const pts = spec.points ?? [];
        if (pts.length < 3 || pts.length > 30)
            return null;
        if (pts.some((p) => !tousFinis(p.x, p.y)))
            return null;
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const x0 = Math.min(...xs, 0);
        const x1 = Math.max(...xs);
        const y0 = Math.min(...ys, 0);
        const y1 = Math.max(...ys);
        if (!(x1 > x0) || !(y1 > y0))
            return null;
        const proj = (p) => vec(((p.x - x0) / (x1 - x0)) * DIAG_L, ((p.y - y0) / (y1 - y0)) * DIAG_H);
        polyligne(b, [vec(0, 0), vec(DIAG_L, 0)], false, 'secondaire');
        polyligne(b, [vec(0, 0), vec(0, DIAG_H)], false, 'secondaire');
        for (const p of pts)
            marque(b, proj(p), 0.013, 'accent');
        if (spec.droiteRegression !== undefined) {
            const { a, c } = { a: spec.droiteRegression.a, c: spec.droiteRegression.b };
            const p0 = proj({ x: x0, y: a * x0 + c });
            const p1 = proj({ x: x1, y: a * x1 + c });
            polyligne(b, [p0, p1], false, 'principal', 'accent', 'pointille');
        }
        axesDiagramme(b, spec);
        return b;
    }
    if (spec.droiteRegression !== undefined)
        return null; // réservé au nuage de points
    const series = spec.series ?? [];
    if (series.length < 2 || series.length > 12)
        return null;
    if (new Set(series.map((s) => s.categorie)).size !== series.length)
        return null;
    if (series.some((s) => s.categorie.length === 0 || s.categorie.length > 12))
        return null;
    if (series.some((s) => !tousFinis(s.valeur)))
        return null;
    if (spec.type === 'bandes' && series.some((s) => s.valeur < 0))
        return null;
    const max = Math.max(...series.map((s) => s.valeur), 0);
    const min = Math.min(...series.map((s) => s.valeur), 0);
    if (!(max > min))
        return null;
    const yDe = (v) => ((v - min) / (max - min)) * DIAG_H;
    const pasX = DIAG_L / series.length;
    polyligne(b, [vec(0, yDe(0)), vec(DIAG_L, yDe(0))], false, 'secondaire');
    polyligne(b, [vec(0, 0), vec(0, DIAG_H)], false, 'secondaire');
    series.forEach((s, i) => {
        const cx = pasX * (i + 0.5);
        if (spec.type === 'bandes') {
            const l = pasX * 0.56;
            const y = yDe(s.valeur);
            const rect = [
                vec(cx - l / 2, yDe(0)),
                vec(cx + l / 2, yDe(0)),
                vec(cx + l / 2, y),
                vec(cx - l / 2, y),
            ];
            polyligne(b, rect, true, 'grille', 'accent', 'plein', true);
            polyligne(b, rect, true, 'principal', 'accent');
            if (spec.valeursAffichees === true) {
                poserTexteFixe(b, vec(cx, y + b.diagonale * 0.03), formaterNombre(s.valeur), 'milieu', 'accent');
            }
        }
        poserTexteFixe(b, vec(cx, yDe(0) - b.diagonale * 0.04), s.categorie, 'milieu');
    });
    if (spec.type === 'ligne_brisee') {
        const pts = series.map((s, i) => vec(pasX * (i + 0.5), yDe(s.valeur)));
        polyligne(b, pts, false, 'principal', 'accent');
        pts.forEach((p, i) => {
            marque(b, p, 0.013, 'accent');
            if (spec.valeursAffichees === true) {
                poserTexteFixe(b, vec(p.x, p.y + b.diagonale * 0.035), formaterNombre(series[i].valeur), 'milieu', 'accent');
            }
        });
    }
    axesDiagramme(b, spec);
    return b;
}
/** Les deux titres d'axe, écrits HORIZONTALEMENT — un titre pivoté n'est pas lisible au lecteur d'écran ni à l'œil. */
function axesDiagramme(b, spec) {
    if (spec.titreAxeX.length > 0) {
        poserTexteFixe(b, vec(DIAG_L / 2, -b.diagonale * 0.11), spec.titreAxeX, 'milieu');
    }
    if (spec.titreAxeY.length > 0) {
        poserTexteFixe(b, vec(0, DIAG_H + b.diagonale * 0.06), spec.titreAxeY, 'debut');
    }
}
/* ------------------------------------------------------- ancrages communs */
function ancragePolygonal(b, anc, positions) {
    switch (anc.type) {
        case 'sommet':
            return positions.get(anc.nom) ?? null;
        case 'cote': {
            const de = positions.get(anc.de);
            const a = positions.get(anc.a);
            return de !== undefined && a !== undefined ? milieu(de, a) : null;
        }
        case 'angle':
            return positions.get(anc.sommet) ?? null;
        case 'element':
            return b.elementsAncres[anc.index] ?? null;
        default:
            return null;
    }
}
function poserEtiquettes(b, etiquettes, resoudreAncrage) {
    for (const e of etiquettes ?? []) {
        const ancre = resoudreAncrage(e.ancrage);
        if (ancre === null)
            continue;
        poserTexte(b, ancre, sortante(b, ancre), e.texte, e.ton ?? 'neutre', 1, PRIORITE_ETIQUETTE.donnee);
    }
}
/* ------------------------------------- couche d'annotations (H1) — jamais validée */
function construireAnnotations(spec, b) {
    const couche = brutVide();
    couche.diagonale = b.diagonale;
    couche.centre = b.centre;
    const positions = new Map(b.points.map((p) => [p.nom, p.position]));
    const ancrage = (anc) => ancragePolygonal(b, anc, positions);
    for (const a of (spec.annotations ?? []).slice(0, 6)) {
        switch (a.type) {
            case 'marque_angle_droit': {
                // La marque qu'Émile a posée au MAUVAIS sommet : une affirmation de
                // l'élève, pas une propriété de la figure. Elle n'est jamais vérifiée.
                const s = positions.get(a.sommet);
                if (s === undefined)
                    break;
                const voisins = [...positions.entries()]
                    .filter(([nom]) => nom !== a.sommet)
                    .map(([, p]) => p);
                if (voisins.length < 2)
                    break;
                marqueAngleDroit(couche, s, voisins[0], voisins[1], a.ton);
                break;
            }
            case 'cote_souligne': {
                const de = positions.get(a.de);
                const q = positions.get(a.a);
                if (de === undefined || q === undefined)
                    break;
                polyligne(couche, [de, q], false, 'principal', a.ton, 'fort');
                if (a.etiquette !== undefined) {
                    // Dégagement doublé : une annotation est POSÉE PAR-DESSUS une figure
                    // qui porte déjà ses propres étiquettes au même endroit.
                    poserTexte(couche, milieu(de, q), normaleSortante(b, de, q), a.etiquette, a.ton, 2.2, PRIORITE_ETIQUETTE.donnee);
                }
                break;
            }
            case 'chemin': {
                const pts = a.points.map((n) => positions.get(n)).filter((p) => p !== undefined);
                if (pts.length < 2)
                    break;
                polyligne(couche, pts, false, 'principal', a.ton, a.style);
                if (a.etiquette !== undefined) {
                    const m = milieu(pts[0], pts[pts.length - 1]);
                    poserTexte(couche, m, sortante(b, m), a.etiquette, a.ton, 2.2, PRIORITE_ETIQUETTE.decor);
                }
                break;
            }
            case 'note': {
                const p = ancrage(a.ancrage);
                if (p === null)
                    break;
                poserTexte(couche, p, sortante(b, p), a.texte, a.ton, 1.4, PRIORITE_ETIQUETTE.donnee);
                break;
            }
        }
    }
    return couche;
}
/**
 * La taille de police est FIXE en unités d'affichage : plus la boîte est
 * large, plus le texte occupe de place en unités naturelles. L'encombrement
 * vertical est ASYMÉTRIQUE, calqué sur ce que le renderer dessine — la ligne
 * de base est décalée, la montante monte plus haut que la descendante ne
 * descend. Un demi-cadratin de part et d'autre sous-réserverait le bas et
 * rognerait les descendantes.
 */
function metriqueTexte(k) {
    return {
        parCaractere: (TAILLE_POLICE_RESOLUE * LARGEUR_CARACTERE) / k,
        versLeHaut: ((MONTANTE - LIGNE_BASE_TEXTE) * TAILLE_POLICE_RESOLUE) / k,
        versLeBas: ((DESCENDANTE + LIGNE_BASE_TEXTE) * TAILLE_POLICE_RESOLUE) / k,
    };
}
function boiteDuTexte(t, m) {
    const largeur = t.texte.length * m.parCaractere;
    const minX = t.alignement === 'debut'
        ? t.position.x
        : t.alignement === 'fin'
            ? t.position.x - largeur
            : t.position.x - largeur / 2;
    return {
        minX,
        maxX: minX + largeur,
        minY: t.position.y - m.versLeBas,
        maxY: t.position.y + m.versLeHaut,
    };
}
/**
 * Les traits contre lesquels une étiquette ne doit pas se poser. **Le
 * quadrillage n'en fait jamais partie** : la maquette écrit ses graduations
 * par-dessus, et une figure dont les étiquettes fuiraient le quadrillage
 * n'aurait plus de place nulle part.
 */
function obstaclesDe(couches) {
    const sortie = [];
    const ECHANTILLONS_ARC = 24;
    for (const couche of couches) {
        for (const t of couche.traces) {
            if (t.trait === 'grille')
                continue;
            const f = t.forme;
            if (f.type === 'polyligne') {
                const pts = f.points;
                for (let i = 1; i < pts.length; i += 1) {
                    sortie.push([pts[i - 1], pts[i]]);
                }
                if (f.fermee && pts.length > 2) {
                    sortie.push([pts[pts.length - 1], pts[0]]);
                }
            }
            else {
                const debut = f.type === 'cercle' ? 0 : f.debutDeg;
                const fin = f.type === 'cercle' ? 360 : f.finDeg;
                let precedent = surCercle(f.centre, f.rayon, debut);
                for (let i = 1; i <= ECHANTILLONS_ARC; i += 1) {
                    const a = debut + ((fin - debut) * i) / ECHANTILLONS_ARC;
                    const courant = surCercle(f.centre, f.rayon, a);
                    sortie.push([precedent, courant]);
                    precedent = courant;
                }
            }
        }
    }
    return sortie;
}
/**
 * Applique le placeur d'étiquettes à une couche : les textes ANCRÉS sont
 * replacés si — et seulement si — leur position préférée est en conflit ; les
 * textes fixes (graduations, catégories) ne bougent jamais et servent de
 * réserves. Rend les boîtes occupées, pour que la couche d'annotations évite
 * ce que la couche de figure a déjà pris.
 *
 * Le tableau `textes` garde exactement son ordre et son contenu : le placeur
 * change des positions, jamais un mot. C'est ce qui garantit que
 * `decrireFigure()` — qui ne lit ni `textes` ni leurs positions — reste
 * identique quoi qu'il arrive à la mise en page.
 */
function placerLaCouche(couche, m, obstacles, reserves, actif) {
    if (!actif) {
        couche.rappels.length = 0;
        return couche.textes.map((t) => boiteDuTexte(t, m));
    }
    const indices = [];
    const aPlacer = [];
    const fixes = [];
    for (let i = 0; i < couche.textes.length; i += 1) {
        const t = couche.textes[i];
        if (t.ancre === null) {
            fixes.push(boiteDuTexte(t, m));
            continue;
        }
        indices.push(i);
        aPlacer.push({
            ancre: t.ancre,
            direction: t.direction,
            degagement: t.degagement,
            largeur: t.texte.length * m.parCaractere,
            versLeHaut: m.versLeHaut,
            versLeBas: m.versLeBas,
            priorite: t.priorite,
        });
    }
    const placements = resoudreCollisionsEtiquettes(aPlacer, obstacles, [...reserves, ...fixes]);
    couche.rappels.length = 0;
    const boites = [...fixes];
    for (let j = 0; j < indices.length; j += 1) {
        const i = indices[j];
        const p = placements[j];
        const t = couche.textes[i];
        couche.textes[i] = { ...t, position: p.position, alignement: p.alignement };
        if (p.rappel !== null)
            couche.rappels.push([p.rappel[0], p.rappel[1]]);
        boites.push(boiteDuTexte(couche.textes[i], m));
    }
    return boites;
}
function pointsDeTrace(t) {
    const f = t.forme;
    if (f.type === 'polyligne')
        return f.points.map((p) => vec(p.x, p.y));
    if (f.type === 'cercle') {
        return [
            vec(f.centre.x - f.rayon, f.centre.y - f.rayon),
            vec(f.centre.x + f.rayon, f.centre.y + f.rayon),
        ];
    }
    const pts = [
        f.secteur ? vec(f.centre.x, f.centre.y) : surCercle(f.centre, f.rayon, f.debutDeg),
    ];
    const pas = 6;
    for (let a = f.debutDeg; a <= f.finDeg; a += pas)
        pts.push(surCercle(f.centre, f.rayon, a));
    pts.push(surCercle(f.centre, f.rayon, f.finDeg));
    return pts;
}
function englober(boite, p) {
    boite.minX = Math.min(boite.minX, p.x);
    boite.maxX = Math.max(boite.maxX, p.x);
    boite.minY = Math.min(boite.minY, p.y);
    boite.maxY = Math.max(boite.maxY, p.y);
}
function appliquerRotation(b, degres) {
    if (degres === 0)
        return;
    const c = b.pivot;
    const tp = (p) => tourner(p, c, degres);
    for (let i = 0; i < b.traces.length; i += 1) {
        const t = b.traces[i];
        const f = t.forme;
        let forme;
        if (f.type === 'polyligne')
            forme = {
                type: 'polyligne',
                points: f.points.map((p) => tp(vec(p.x, p.y))),
                fermee: f.fermee,
            };
        else if (f.type === 'cercle')
            forme = { type: 'cercle', centre: tp(vec(f.centre.x, f.centre.y)), rayon: f.rayon };
        else
            forme = {
                type: 'arc',
                centre: tp(vec(f.centre.x, f.centre.y)),
                rayon: f.rayon,
                debutDeg: f.debutDeg + degres,
                finDeg: f.finDeg + degres,
                secteur: f.secteur,
            };
        b.traces[i] = { ...t, forme };
    }
    for (let i = 0; i < b.marques.length; i += 1) {
        const m = b.marques[i];
        b.marques[i] = { ...m, position: tp(vec(m.position.x, m.position.y)) };
    }
    // Les ANCRES de texte tournent avec la géométrie ; les textes, jamais (H4).
    // La direction préférée est un VECTEUR : elle tourne autour de l'origine,
    // sans translation — sinon la normale sortante cesserait de sortir.
    const tv = (v) => tourner(v, vec(0, 0), degres);
    for (let i = 0; i < b.textes.length; i += 1) {
        const t = b.textes[i];
        b.textes[i] = {
            ...t,
            position: tp(t.position),
            ancre: t.ancre === null ? null : tp(t.ancre),
            direction: tv(t.direction),
        };
    }
    for (let i = 0; i < b.points.length; i += 1) {
        const p = b.points[i];
        b.points[i] = { ...p, position: tp(p.position) };
    }
}
function transformer(b, k, boite) {
    const conv = (p) => ({
        x: (p.x - boite.minX) * k,
        y: (boite.maxY - p.y) * k,
    });
    return {
        traces: b.traces.map((t) => {
            const f = t.forme;
            let forme;
            if (f.type === 'polyligne')
                forme = { type: 'polyligne', points: f.points.map(conv), fermee: f.fermee };
            else if (f.type === 'cercle')
                forme = { type: 'cercle', centre: conv(f.centre), rayon: f.rayon * k };
            else
                forme = {
                    type: 'arc',
                    centre: conv(f.centre),
                    rayon: f.rayon * k,
                    debutDeg: f.debutDeg,
                    finDeg: f.finDeg,
                    secteur: f.secteur,
                };
            return { ...t, forme };
        }),
        marques: b.marques.map((m) => ({
            ...m,
            position: conv(m.position),
            rayon: Math.max(2.4, m.rayon * k),
        })),
        textes: b.textes.map((t) => ({
            position: conv(t.position),
            texte: t.texte,
            alignement: t.alignement,
            ton: t.ton,
        })),
        rappels: b.rappels.map(([de, a]) => ({ de: conv(de), a: conv(a) })),
    };
}
/**
 * Résout une spécification bornée en liste d'affichage.
 *
 * @returns `null` — motif `insoluble` (§3.4) — dès qu'aucune figure ne
 * satisfait les données. Le refus est explicite et bruyant : l'appelant
 * journalise le motif, relance le modèle, puis sert l'exercice SANS figure.
 * Jamais un rattrapage silencieux.
 */
/* ---------------------------------------------------------------- solide */
/**
 * Un solide, projeté dans le plan.
 *
 * Deux vues, deux chemins : la PERSPECTIVE, où les arêtes cachées sont
 * calculées puis tracées en pointillé, et le DÉVELOPPEMENT, où la surface est
 * mise à plat avec ses lignes de pliage.
 *
 * Aucune longueur affichée n'est reprise de la spécification sans passer par
 * la géométrie : l'apothème d'un cône est déduit de son rayon et de sa
 * hauteur, jamais fourni. C'est la règle 1 du contrat, et elle est ce qui
 * empêche une étiquette de mentir sur la figure qu'elle annote.
 */
function resoudreSolide(spec) {
    const b = brutVide();
    /* Une figure à trois dimensions ne pivote pas : la faire tourner dans le
       plan casserait la lecture de la projection, dont tout l'intérêt est que
       les fuyantes gardent un angle constant. Même choix que le repère et le
       diagramme. */
    b.rotationPermise = false;
    b.unite = spec.dimensions.unite;
    const ok = spec.vue === 'developpement' ? tracerDeveloppement(b, spec) : tracerPerspective(b, spec);
    if (!ok)
        return null;
    cadrerSurLesTraces(b);
    if (spec.mesuresAffichees !== false)
        etiqueterSolide(b, spec);
    return b;
}
/** Recentre la figure et mesure sa diagonale, à partir de ce qui a été tracé. */
function cadrerSurLesTraces(b) {
    const pts = [];
    for (const t of b.traces) {
        if (t.forme.type === 'polyligne')
            pts.push(...t.forme.points);
        else
            pts.push(t.forme.centre);
    }
    if (pts.length === 0)
        return;
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    b.centre = vec((minX + maxX) / 2, (minY + maxY) / 2);
    b.pivot = b.centre;
    b.diagonale = Math.max(Math.hypot(maxX - minX, maxY - minY), 1e-6);
}
function tracerPerspective(b, spec) {
    const d = spec.dimensions;
    const pointille = spec.aretesCacheesVisibles !== false;
    const polyedre = polyedreDe(d);
    if (polyedre !== null) {
        for (const a of classerAretes(polyedre)) {
            const p = polyedre.sommets.get(a.de);
            const q = polyedre.sommets.get(a.a);
            if (p === undefined || q === undefined)
                return false;
            // Une arête cachée qu'on ne trace pas est simplement absente : c'est ce
            // qu'on veut quand l'exercice demande à l'élève de la retrouver.
            if (a.cachee && !pointille)
                continue;
            segment(b, projeter(p), projeter(q), 'principal', 'neutre', a.cachee ? 'pointille' : 'plein');
        }
        tracerReperes(b, spec);
        return true;
    }
    if (d.forme === 'sphere') {
        if (!(d.rayon > 0))
            return false;
        trace(b, { type: 'cercle', centre: vec(0, 0), rayon: d.rayon }, 'principal');
        // L'équateur : moitié avant en plein, moitié arrière en pointillé. C'est
        // lui qui fait lire une boule plutôt qu'un disque.
        const eq = ellipseHorizontale(d.rayon, 0);
        polyligne(b, eq.devant, false);
        if (pointille)
            polyligne(b, eq.derriere, false, 'principal', 'neutre', 'pointille');
        tracerReperes(b, spec);
        return true;
    }
    if (d.forme === 'cylindre') {
        if (!(d.rayon > 0) || !(d.hauteur > 0))
            return false;
        const haut = ellipseHorizontale(d.rayon, d.hauteur);
        const bas = ellipseHorizontale(d.rayon, 0);
        // Le disque du haut est vu en entier ; celui du bas est masqué par le corps.
        polyligne(b, haut.devant, false);
        polyligne(b, haut.derriere, false);
        polyligne(b, bas.devant, false);
        if (pointille)
            polyligne(b, bas.derriere, false, 'principal', 'neutre', 'pointille');
        for (const t of tangentesBase(d.rayon, 0)) {
            segment(b, projeter(t), projeter({ ...t, y: d.hauteur }));
        }
        tracerReperes(b, spec);
        return true;
    }
    /* Cône — le dernier cas. Le typage ne peut pas déduire que les polyèdres
       ont déjà été traités plus haut (`polyedreDe` rend `null` pour les corps de
       révolution, mais ce n'est pas un prédicat de type), d'où ce garde
       explicite. Il ne coûte rien et il dit ce qui reste. */
    if (d.forme !== 'cone')
        return false;
    if (!(d.rayon > 0) || !(d.hauteur > 0))
        return false;
    const base = ellipseHorizontale(d.rayon, 0);
    polyligne(b, base.devant, false);
    if (pointille)
        polyligne(b, base.derriere, false, 'principal', 'neutre', 'pointille');
    const sommet = projeter({ x: 0, y: d.hauteur, z: 0 });
    for (const t of tangentesBase(d.rayon, 0))
        segment(b, projeter(t), sommet);
    b.points.push({ nom: 'S', position: sommet });
    tracerReperes(b, spec);
    return true;
}
/** Hauteur, rayon, apothème — tracés en pointillé, en appui sur la figure. */
function tracerReperes(b, spec) {
    const d = spec.dimensions;
    const reperes = new Set(spec.reperes ?? []);
    if (reperes.size === 0)
        return;
    /* Aiguillage explicite plutôt qu'une chaîne de ternaires : une sphère n'a
       pas de hauteur et un cube n'a pas de rayon, et le typage refuse — à juste
       titre — qu'on aille les chercher sur l'union entière. */
    let hauteur = 0;
    let rayon = 0;
    switch (d.forme) {
        case 'cube':
            hauteur = d.arete;
            break;
        case 'sphere':
            rayon = d.rayon;
            break;
        case 'cylindre':
        case 'cone':
            hauteur = d.hauteur;
            rayon = d.rayon;
            break;
        default:
            hauteur = d.hauteur;
    }
    if (reperes.has('hauteur') && hauteur > 0) {
        segment(b, projeter({ x: 0, y: 0, z: 0 }), projeter({ x: 0, y: hauteur, z: 0 }), 'secondaire', 'accent', 'pointille');
    }
    if (reperes.has('rayon') && rayon > 0) {
        segment(b, projeter({ x: 0, y: 0, z: 0 }), projeter({ x: rayon, y: 0, z: 0 }), 'secondaire', 'accent', 'pointille');
    }
    if (reperes.has('apotheme') && d.forme === 'cone') {
        const [t] = tangentesBase(d.rayon, 0);
        segment(b, projeter(t), projeter({ x: 0, y: d.hauteur, z: 0 }), 'secondaire', 'accent', 'pointille');
    }
}
function tracerDeveloppement(b, spec) {
    const dev = developpementDe(spec.dimensions);
    // `null` sur une sphère : sa surface n'est pas développable.
    if (dev === null)
        return false;
    for (const c of dev.contours)
        polyligne(b, c, true);
    for (const c of dev.cercles)
        trace(b, { type: 'cercle', centre: c.centre, rayon: c.rayon }, 'principal');
    for (const s of dev.secteurs) {
        trace(b, {
            type: 'arc',
            centre: s.centre,
            rayon: s.rayon,
            debutDeg: s.debutDeg,
            finDeg: s.finDeg,
            secteur: true,
        }, 'principal');
    }
    for (const [p, q] of dev.plis)
        segment(b, p, q, 'secondaire', 'neutre', 'pointille');
    return true;
}
/** Écrit les dimensions données, et elles seules. */
function etiqueterSolide(b, spec) {
    const d = spec.dimensions;
    const u = d.unite;
    const dire = (valeur, quoi) => `${quoi} ${formaterNombre(valeur)} ${u}`;
    const lignes = [];
    switch (d.forme) {
        case 'cube':
            lignes.push(dire(d.arete, 'a ='));
            break;
        case 'sphere':
            lignes.push(dire(d.rayon, 'r ='));
            break;
        case 'cylindre':
        case 'cone':
            lignes.push(dire(d.rayon, 'r ='), dire(d.hauteur, 'h ='));
            break;
        default:
            lignes.push(dire(d.coteBase, 'c ='), dire(d.hauteur, 'h ='));
            if (d.profondeurBase !== undefined)
                lignes.push(dire(d.profondeurBase, 'p ='));
    }
    /* Posées au-dessus de la figure plutôt qu'ancrées sur une arête : sur une
       projection, une étiquette collée à une fuyante se lit à côté d'une autre
       arête et désigne la mauvaise longueur. */
    lignes.forEach((texte, i) => {
        poserTexteFixe(b, vec(b.centre.x, b.centre.y + b.diagonale * (0.58 + i * 0.09)), texte, 'milieu');
    });
}
export function resoudreFigure(spec, options = {}) {
    let b;
    switch (spec.famille) {
        case 'triangle':
            b = resoudreTriangle(spec);
            break;
        case 'cercle':
            b = resoudreCercle(spec);
            break;
        case 'polygone':
            b = resoudrePolygone(spec);
            break;
        case 'repere':
            b = resoudreRepere(spec);
            break;
        case 'diagramme':
            b = resoudreDiagramme(spec);
            break;
        case 'solide':
            b = resoudreSolide(spec);
            break;
        default:
            return null;
    }
    if (b === null)
        return null;
    const annotations = construireAnnotations(spec, b);
    const rotation = (((spec.rotationDeg ?? 0) % 360) + 360) % 360;
    if (rotation !== 0) {
        if (!b.rotationPermise)
            return null; // un repère ou un diagramme tourné mentirait sur ses axes
        appliquerRotation(b, rotation);
        annotations.pivot = b.pivot;
        appliquerRotation(annotations, rotation);
    }
    // Boîte : la géométrie, ses annotations, puis l'encombrement des textes.
    const boite = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
    for (const couche of [b, annotations]) {
        for (const t of couche.traces)
            for (const p of pointsDeTrace(t))
                englober(boite, p);
        for (const m of couche.marques)
            englober(boite, m.position);
    }
    if (!tousFinis(boite.minX, boite.maxX, boite.minY, boite.maxY))
        return null;
    if (boite.maxX - boite.minX < 1e-9 && boite.maxY - boite.minY < 1e-9)
        return null;
    const etendue = Math.max(boite.maxX - boite.minX, boite.maxY - boite.minY, 1e-6);
    const marge = etendue * MARGE;
    boite.minX -= marge;
    boite.maxX += marge;
    boite.minY -= marge;
    boite.maxY += marge;
    // Placement des étiquettes, puis encombrement des textes. La taille de
    // police est FIXE en unités d'affichage : élargir la boîte réduit l'échelle,
    // ce qui agrandit d'autant l'encombrement des textes en unités naturelles.
    // Une seule passe sous-estime donc systématiquement, et une étiquette de
    // bord se retrouve rognée. Trois passes suffisent à converger — la boîte ne
    // fait que croître.
    //
    // Le placeur (H5) est rejoué à chaque passe, sur la métrique de la passe :
    // sa candidate n°0 EST la position de la normale sortante, donc une figure
    // sans conflit ressort de cette boucle exactement comme avant qu'elle
    // existe. Les obstacles, eux, sont calculés une seule fois : la géométrie
    // ne bouge plus.
    const placeur = options.placerLesEtiquettes !== false;
    const obstacles = placeur ? obstaclesDe([b, annotations]) : [];
    let k = LARGEUR_RESOLUE / Math.max(boite.maxX - boite.minX, 1e-6);
    for (let passe = 0; passe < 3; passe += 1) {
        const m = metriqueTexte(k);
        const boitesFigure = placerLaCouche(b, m, obstacles, [], placeur);
        // La couche d'annotations est posée PAR-DESSUS : elle évite ce que la
        // figure a déjà pris, jamais l'inverse. Sans cette asymétrie, la présence
        // d'une annotation déplacerait les étiquettes de la figure — or les deux
        // couches sont physiquement séparées (H1).
        const boitesAnnotations = placerLaCouche(annotations, m, obstacles, boitesFigure, placeur);
        for (const bt of [...boitesFigure, ...boitesAnnotations]) {
            englober(boite, vec(bt.minX, bt.minY));
            englober(boite, vec(bt.maxX, bt.maxY));
        }
        k = LARGEUR_RESOLUE / Math.max(boite.maxX - boite.minX, 1e-6);
    }
    // Rapport d'aspect borné en ÉLARGISSANT la boîte — jamais en déformant la
    // géométrie : les échelles x et y restent égales (H8).
    let largeurN = boite.maxX - boite.minX;
    let hauteurN = boite.maxY - boite.minY;
    if (hauteurN / largeurN > RATIO_MAX) {
        const cible = hauteurN / RATIO_MAX;
        const ajout = (cible - largeurN) / 2;
        boite.minX -= ajout;
        boite.maxX += ajout;
    }
    else if (hauteurN / largeurN < RATIO_MIN) {
        const cible = largeurN * RATIO_MIN;
        const ajout = (cible - hauteurN) / 2;
        boite.minY -= ajout;
        boite.maxY += ajout;
    }
    largeurN = boite.maxX - boite.minX;
    hauteurN = boite.maxY - boite.minY;
    k = LARGEUR_RESOLUE / largeurN;
    if (!tousFinis(k) || k <= 0)
        return null;
    return {
        famille: spec.famille,
        boite: { largeur: LARGEUR_RESOLUE, hauteur: Number((hauteurN * k).toFixed(3)) },
        echelle: {
            parUnite: k,
            origineX: -boite.minX * k,
            origineY: boite.maxY * k,
            unite: b.unite,
        },
        points: b.points.map((p) => ({
            nom: p.nom,
            position: { x: (p.position.x - boite.minX) * k, y: (boite.maxY - p.position.y) * k },
        })),
        figure: transformer(b, k, boite),
        annotations: transformer(annotations, k, boite),
    };
}
//# sourceMappingURL=resoudre.js.map